/**
 * Per-day DRAFT quantities for the school weekly order surface.
 *
 * Typing a quantity in an expanded day card writes here, NOT to the cart —
 * the draft is committed only when the admin clicks "Add meals to cart"
 * (which flushes the day's draft into the persistent `school-cart` store).
 * The collapsed-card blue dot reflects a day whose draft differs from what's
 * committed in the cart.
 *
 * Drafts are keyed by `cartLineKey` (date::mealtime::column::menuItem) under
 * each location, and persisted to sessionStorage so an in-progress order
 * survives reloads within a session.
 */

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { mealQty, migrateDraftLines, type SchoolCartLine } from '@/lib/school-orders/cart'

interface SchoolOrderDraftState {
  /** locationId → (cartLineKey → draft line). */
  draftsByLocation: Record<string, Record<string, SchoolCartLine>>
  /**
   * Days already seeded with percentage suggestions this session
   * (locationId → date → true). A seeded day is never re-seeded, so
   * clearing or zeroing every suggestion doesn't bring them back.
   */
  suggestionSeededDays: Record<string, Record<string, true>>
  /** Upsert a draft line; quantity <= 0 removes it. */
  setDraftLine: (line: SchoolCartLine) => void
  /**
   * Seed a day's draft from existing lines (typically the cart's current
   * lines for that day) ONLY when the day has no draft yet — so re-expanding
   * a committed day pre-fills the inputs without clobbering in-progress edits.
   */
  ensureDaySeeded: (locationId: string, date: string, lines: readonly SchoolCartLine[]) => void
  /**
   * Seed a day with by-component percentage suggestions — at most once per
   * (location, day) per session, and never over existing draft edits.
   */
  seedSuggestedDay: (locationId: string, date: string, lines: readonly SchoolCartLine[]) => void
  /** Remove only the still-suggested lines of a day (the "Clear" action). */
  clearSuggestedForDay: (locationId: string, date: string) => void
  /**
   * Live-sync DERIVED suggestion lines (milk quantities that track the meal
   * counts): upsert each line where the existing draft line is absent or
   * still `suggested`, delete a still-suggested line whose derived quantity
   * dropped to 0, and NEVER touch a line the school has edited (not
   * suggested). No-ops (state identity preserved) when nothing changes, so
   * it is safe to call from an effect that itself depends on the draft.
   */
  syncDerivedLines: (locationId: string, lines: readonly SchoolCartLine[]) => void
  /**
   * Apply a draft-key reconciliation (`reconcileDraftDay`): move each line
   * to its rewritten key and drop stranded machine-derived lines. A move
   * whose source is gone or whose target is now occupied is skipped —
   * quantities are never merged. No-op (state identity preserved) when
   * nothing applies, so it is safe to call from an effect that itself
   * depends on the draft.
   */
  applyDraftReconcile: (
    locationId: string,
    reconcile: { moves: ReadonlyArray<{ fromKey: string; line: SchoolCartLine }>; dropKeys: readonly string[] },
  ) => void
  /**
   * The day-card "Reset" action: drop the day's ENTIRE draft (edits and
   * suggestions alike) and un-mark the once-per-session seed gate so the
   * prefill effect re-seeds the day fresh from history + defaults.
   */
  resetDay: (locationId: string, date: string) => void
  /** Drop a day's entire draft. */
  clearDay: (locationId: string, date: string) => void
}

export const useSchoolOrderDraft = create<SchoolOrderDraftState>()(
  persist(
    (set, get) => ({
      draftsByLocation: {},
      suggestionSeededDays: {},
      setDraftLine: (line) => {
        const forLocation = { ...(get().draftsByLocation[line.locationId] ?? {}) }
        if (line.quantity <= 0) {
          delete forLocation[line.key]
        } else {
          forLocation[line.key] = line
        }
        set({ draftsByLocation: { ...get().draftsByLocation, [line.locationId]: forLocation } })
      },
      ensureDaySeeded: (locationId, date, lines) => {
        const forLocation = get().draftsByLocation[locationId] ?? {}
        const hasDayDraft = Object.values(forLocation).some((l) => l.date === date)
        if (hasDayDraft) {
          return
        }
        const seeded = { ...forLocation }
        for (const line of lines) {
          if (line.quantity > 0) {
            seeded[line.key] = line
          }
        }
        set({ draftsByLocation: { ...get().draftsByLocation, [locationId]: seeded } })
      },
      syncDerivedLines: (locationId, lines) => {
        const state = get()
        const forLocation = state.draftsByLocation[locationId] ?? {}
        let next: Record<string, SchoolCartLine> | null = null
        for (const line of lines) {
          const existing = forLocation[line.key]
          if (existing && !existing.suggested) {
            continue // the school's own edit wins — derivation stops tracking
          }
          if (line.quantity <= 0) {
            if (existing) {
              next = next ?? { ...forLocation }
              delete next[line.key]
            }
            continue
          }
          if (!existing || existing.quantity !== line.quantity) {
            next = next ?? { ...forLocation }
            next[line.key] = line
          }
        }
        if (next) {
          set({ draftsByLocation: { ...state.draftsByLocation, [locationId]: next } })
        }
      },
      seedSuggestedDay: (locationId, date, lines) => {
        const state = get()
        if (state.suggestionSeededDays[locationId]?.[date]) {
          return
        }
        // Mark the attempt even when the day already has manual edits, so
        // deleting those edits later never resurrects the suggestions.
        const seededDays = {
          ...state.suggestionSeededDays,
          [locationId]: { ...(state.suggestionSeededDays[locationId] ?? {}), [date]: true as const },
        }
        const forLocation = state.draftsByLocation[locationId] ?? {}
        const hasDayDraft = Object.values(forLocation).some((l) => l.date === date)
        if (hasDayDraft) {
          set({ suggestionSeededDays: seededDays })
          return
        }
        const seeded = { ...forLocation }
        for (const line of lines) {
          if (line.quantity > 0) {
            seeded[line.key] = line
          }
        }
        set({
          suggestionSeededDays: seededDays,
          draftsByLocation: { ...state.draftsByLocation, [locationId]: seeded },
        })
      },
      applyDraftReconcile: (locationId, reconcile) => {
        const state = get()
        const forLocation = state.draftsByLocation[locationId]
        if (!forLocation) {
          return
        }
        let next: Record<string, SchoolCartLine> | null = null
        for (const key of reconcile.dropKeys) {
          if ((next ?? forLocation)[key]) {
            next = next ?? { ...forLocation }
            delete next[key]
          }
        }
        for (const move of reconcile.moves) {
          const view = next ?? forLocation
          if (!view[move.fromKey] || view[move.line.key]) {
            continue
          }
          next = next ?? { ...forLocation }
          delete next[move.fromKey]
          next[move.line.key] = move.line
        }
        if (next) {
          set({ draftsByLocation: { ...state.draftsByLocation, [locationId]: next } })
        }
      },
      resetDay: (locationId, date) => {
        const state = get()
        const forLocation = state.draftsByLocation[locationId] ?? {}
        const next: Record<string, SchoolCartLine> = {}
        for (const [key, line] of Object.entries(forLocation)) {
          if (line.date !== date) {
            next[key] = line
          }
        }
        const seededForLocation = { ...(state.suggestionSeededDays[locationId] ?? {}) }
        delete seededForLocation[date]
        set({
          draftsByLocation: { ...state.draftsByLocation, [locationId]: next },
          suggestionSeededDays: { ...state.suggestionSeededDays, [locationId]: seededForLocation },
        })
      },
      clearSuggestedForDay: (locationId, date) => {
        const forLocation = get().draftsByLocation[locationId]
        if (!forLocation) {
          return
        }
        const next: Record<string, SchoolCartLine> = {}
        for (const [key, line] of Object.entries(forLocation)) {
          if (!(line.date === date && line.suggested)) {
            next[key] = line
          }
        }
        set({ draftsByLocation: { ...get().draftsByLocation, [locationId]: next } })
      },
      clearDay: (locationId, date) => {
        const forLocation = get().draftsByLocation[locationId]
        if (!forLocation) {
          return
        }
        const next: Record<string, SchoolCartLine> = {}
        for (const [key, line] of Object.entries(forLocation)) {
          if (line.date !== date) {
            next[key] = line
          }
        }
        set({ draftsByLocation: { ...get().draftsByLocation, [locationId]: next } })
      },
    }),
    {
      name: 'ordo.school-order-draft',
      storage: createJSONStorage(() => sessionStorage),
      // v0 → v1: same percentage → quantity conversion the cart store does
      // (`migrateCartLine`). Drafts persist too, so a half-entered day
      // survives the deploy with its component numbers intact.
      version: 1,
      migrate: (persisted, version) =>
        version >= 1 ? (persisted as SchoolOrderDraftState) : migrateDraftLines(persisted as SchoolOrderDraftState),
    },
  ),
)

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const EMPTY_MAP: Record<string, SchoolCartLine> = {}

export const selectDraftMapFor = (locationId: string) => (s: SchoolOrderDraftState) =>
  s.draftsByLocation[locationId] ?? EMPTY_MAP

/** All draft lines for a (location, date). */
export function draftLinesForDay(draftMap: Record<string, SchoolCartLine>, date: string): SchoolCartLine[] {
  return Object.values(draftMap).filter((l) => l.date === date)
}

/**
 * Sum of draft MEAL quantities for a (location, date) — the "N meals
 * selected" count. System-milk lines are excluded: they derive from the
 * meal counts (1 milk per meal at the default 100% beverage rate), so
 * summing them read every entry back doubled — "40 meals selected" for
 * 20 typed (Larkspur, launch week).
 */
export function draftMealCountForDay(draftMap: Record<string, SchoolCartLine>, date: string): number {
  return mealQty(draftLinesForDay(draftMap, date))
}

/** Count of still-suggested (untouched prefill) lines for a (location, date). */
export function suggestedCountForDay(draftMap: Record<string, SchoolCartLine>, date: string): number {
  return draftLinesForDay(draftMap, date).filter((l) => l.suggested).length
}
